import { isRecord } from '../core/utils/is-record';

const GENERIC_MESSAGE = 'Request failed';
const NETWORK_MESSAGE = 'Unable to reach the server. Check your connection and try again.';

/** Typed error fields from the Hub envelope (`code`, `action`, `detail`). */
type ApiErrorFields = { code?: string; action?: string; detail?: unknown };

/**
 * A failed Hub request, mapped from the Hub's error envelope (`status`, `errors`, `code`, `action`, `detail`).
 * Components branch on `code` (e.g. `pro_required`) and show `message` (the Hub's `errors[0]`).
 * Deliberately keeps nothing else from the HttpErrorResponse (no headers, URL or raw body).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly errors: string[];
  readonly code?: string;
  readonly action?: string;
  /** Extra error data from the Hub (e.g. validation constraints). Data only: never render it with `[innerHTML]`. */
  readonly detail?: unknown;

  constructor(status: number, errors: string[], fields: ApiErrorFields = {}, fallbackMessage: string = GENERIC_MESSAGE) {
    super(errors[0] ?? fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
    this.code = fields.code;
    this.action = fields.action;
    this.detail = fields.detail;
  }

  /**
   * Maps whatever a failed request threw into an ApiError. The Hub envelope is recognised by its shape,
   * not by class, so this stays free of Angular imports.
   */
  static fromHttpError(err: unknown): ApiError {
    if (err instanceof ApiError) {
      return err;
    }
    if (!isHttpFailure(err)) {
      // A client-side bug (e.g. a TypeError in a model constructor). Its message is internal, so it is not copied.
      return new ApiError(0, [], { code: 'CLIENT_ERROR' });
    }
    if (err.status === 0) {
      return new ApiError(0, [], { code: 'NETWORK_ERROR' }, NETWORK_MESSAGE);
    }
    return isEnvelope(err.error) ? fromEnvelope(err.status, err.error) : new ApiError(err.status, []);
  }
}

/** The shape of Angular's HttpErrorResponse that matters here: the HTTP status and the response body. */
function isHttpFailure(err: unknown): err is { status: number; error: unknown } {
  return isRecord(err) && typeof err['status'] === 'number' && 'error' in err;
}

function isEnvelope(body: unknown): body is Record<string, unknown> {
  return isRecord(body) && ('errors' in body || 'code' in body);
}

function fromEnvelope(status: number, body: Record<string, unknown>): ApiError {
  const errors = Array.isArray(body['errors'])
    ? body['errors'].filter((e): e is string => typeof e === 'string')
    : [];
  return new ApiError(status, errors, {
    code: optionalString(body['code']),
    action: optionalString(body['action']),
    detail: body['detail'],
  });
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
